import { ComponentType } from 'react'

import { WidgetMeta } from './widget-types'

/**
 * Creates a widget for the widget Registry.
 *
 * DevNote:
 * Allows us to provide proptypes to additional meta options.
 */

type StringLiteralError = 'this requires a string literal'
type StringLiteral<T extends string> = string extends T ? StringLiteralError : T

export type ErrorRendererProps = {
  message?: string
}

export const createWidget = <
  Props = {},
  WidgetName extends string = 'unknown',
  Slots = [],
>(
  widget: ComponentType<Props>,
  options: {
    type: StringLiteral<WidgetName>
    slots?: Slots[]
    errorRenderer?: ComponentType<ErrorRendererProps>
  } & WidgetMeta<WidgetName, Props>,
) => ({ widget, ...options })

export type WidgetRegistryEntry = {
  widget: ComponentType<any>
  errorRenderer?: ComponentType<ErrorRendererProps>
} & WidgetMeta<any, any>

/**
 * Creates a registry of widgets and widget options defined by
 * `createWidget()`.
 */
export const createWidgetRegistry = <T extends WidgetRegistryEntry[]>(
  widgets: T,
): { [K in T[number]['type']]: Extract<T[number], { type: K }> } =>
  Object.values(widgets).reduce(
    (prev, exported) => ({
      ...prev,
      [exported.type]: exported,
    }),
    {} as any,
  )

// as Record<string, WidgetRegistryEntry>
